import { useCallback, useState } from 'react'
import { authClient } from '../lib/auth-client'
import { logError } from '../lib/logError'
import type { OperatorUser } from '../lib/auth-client'

export function useTotpEnrollment(user: OperatorUser | null) {
  const [totpURI, setTotpURI] = useState<string | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [enabled, setEnabled] = useState(Boolean(user?.twoFactorEnabled))

  const refresh = useCallback(async () => {
    const { data } = await authClient.getSession()
    const on = Boolean(
      (data?.user as { twoFactorEnabled?: boolean } | undefined)?.twoFactorEnabled,
    )
    setEnabled(on)
  }, [])

  const startEnable = useCallback(async () => {
    try {
      // allowPasswordless on the server — no password for magic-link users.
      const { data, error } = await authClient.twoFactor.enable({
        issuer: 'BeyondQuota',
      } as Parameters<typeof authClient.twoFactor.enable>[0])
      if (error) throw new Error(error.message || "Couldn't start authenticator setup")
      setTotpURI(data?.totpURI ?? null)
      setBackupCodes(data?.backupCodes ?? null)
      return data
    } catch (err) {
      logError('totp.enable', err)
      throw err
    }
  }, [])

  const confirmEnable = useCallback(
    async (code: string) => {
      try {
        const { error } = await authClient.twoFactor.verifyTotp({ code })
        if (error) throw new Error(error.message || 'Invalid authenticator code')
        setTotpURI(null)
        await refresh()
      } catch (err) {
        logError('totp.confirm', err)
        throw err
      }
    },
    [refresh],
  )

  const disable = useCallback(async () => {
    try {
      const { error } = await authClient.twoFactor.disable(
        {} as Parameters<typeof authClient.twoFactor.disable>[0],
      )
      if (error) throw new Error(error.message || "Couldn't disable authenticator")
      setBackupCodes(null)
      await refresh()
    } catch (err) {
      logError('totp.disable', err)
      throw err
    }
  }, [refresh])

  return {
    enabled,
    totpURI,
    backupCodes,
    startEnable,
    confirmEnable,
    disable,
    refresh,
    clearSetup: () => {
      setTotpURI(null)
    },
  }
}
